import EbayConnectCard, {
  ShopifyConnectCard,
} from "@/components/dashboard/connect-card";
import { IntegrationsGrid } from "@/components/dashboard/integrations-grid";
import AnalyticsSummary from "@/components/dashboard/analytics-summary";
import { MainTitle } from "@/components/main-title";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import GatedOverlay from "@/components/ui/gated-overlay";
import { getConnectUrl } from "@/hooks/ebay";
import { isGated } from "@/hooks/gated";
import { analyticsQueryOpts } from "@/hooks/analytics";
import { platformsQueryOpts, usePlatformSyncToggle } from "@/hooks/platform";

import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod/v4";
import { useOrg } from "@/providers/organization";
import EbayDialog from "@/components/dashboard/ebay-dialog";
import { useEffect } from "react";
import { InfoIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

const dashboardSearch = z.object({
  status: z.literal("success").optional(),
  message: z.string().optional(),
  onboarded: z.boolean().optional(),
  platform: z.enum(["shopify", "ebay", "tiktok"]).optional(),
});

export const Route = createFileRoute("/$org/dashboard")({
  validateSearch: dashboardSearch,
  component: RouteComponent,
  loader: async ({ context }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    const gated = await isGated(
      {
        token: getToken() as string,
        orgId: orgId,
        setToken,
      },
      handleTokenInvalid,
    );

    context.queryClient.ensureQueryData(
      platformsQueryOpts({
        setToken,
        orgId: orgId,
        token: getToken() as string,
      }),
    );

    context.queryClient.ensureQueryData(
      analyticsQueryOpts({
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    try {
      const { url } = await getConnectUrl({
        setToken,
        orgId: orgId as string,
        token: getToken() as string,
      });
      return { url, gated: gated?.is_gated };
    } catch (e) {
      console.log(e);
      return { url: "", gated: gated?.is_gated };
    }
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { status, onboarded, platform } = Route.useSearch();
  const { gated } = Route.useLoaderData();
  const { org } = Route.useParams();
  const { url } = Route.useLoaderData();
  const { getToken, setToken } = useAuth();
  const { orgId } = useOrg();
  const resolvedOrgId = getOrgIdWithFallback(orgId);
  const { data: platforms } = useQuery(
    platformsQueryOpts({
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
  );
  const syncToggleMutation = usePlatformSyncToggle();

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  useEffect(() => {
    if (status) {
      toast.success(t("dashboard.toast.subscribed"));
    }

    if (platform === "shopify") {
      toast.success(t("dashboard.toast.shopifyConnected"));
    } else if (platform === "ebay") {
      toast.success(t("dashboard.toast.ebayConnected"));
    } else if (platform === "tiktok") {
      toast.success(t("dashboard.toast.tiktokConnected"));
    }
  }, [status, platform]);

  return (
    <div>
      <MainTitle title={t("nav.dashboard")} />
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="max-w-4xl mx-auto space-y-4">
          <Alert>
            <InfoIcon />
            <AlertTitle>{t("dashboard.exportBeta.title")}</AlertTitle>
            <AlertDescription>
              {t("dashboard.exportBeta.description")}
            </AlertDescription>
          </Alert>

          <AnalyticsSummary />

          <IntegrationsGrid
            data={platforms ?? []}
            url={url as string}
            onSyncToggle={(id) => syncToggleMutation.mutate(id)}
            org={org}
          />
          <div className="flex flex-wrap gap-4">
            <EbayConnectCard platform="ebay" />
            {/*<TiktokConnectCard /> */}
            <ShopifyConnectCard platform="shopify" />
          </div>
        </div>
      </div>
      {onboarded && <EbayDialog platform="ebay" />}
    </div>
  );
}
